import * as React from 'react';
import { useCallback, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Controller, useForm, UseFormReturn } from 'react-hook-form';
import { Location, To, useBeforeUnload, useBlocker, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { faEllipsis } from '@fortawesome/pro-regular-svg-icons';
import { isEqual } from 'lodash';
import Button from '../../core/components/Button.tsx';
import ConfirmModal from '../../core/components/ConfirmModal.tsx';
import DropdownMenu from '../../core/components/DropdownMenu.tsx';
import SelectField from '../../core/components/Fields/SelectField.tsx';
import TagsField from '../../core/components/Fields/TagsField.tsx';
import TextField from '../../core/components/Fields/TextField.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import { DateRuleListField } from '../../core/components/Rules';
import { SettingsCard } from '../../core/components/SettingsFormCard.tsx';
import { useToaster } from '../../core/components/Toast.tsx';
import { url } from '../../core/utils/link.ts';
import { DEFAULT_LINE_ITEM_TYPE, DocumentType } from '../constants.ts';
import { GROUP_SEPARATOR } from '../pages/Assistance/useDocumentConfig';
import DeliveryDateRangeReplacementSelectField from './DeliveryDateRangeReplacementSelectField.tsx';
import DocumentNumberFallbackSelectField from './DocumentNumberFallbackSelectField.tsx';
import { useFieldAssistance } from './FieldAssistance.tsx';
import FieldsConfig, { FieldsConfigRowData } from './FieldsConfig.tsx';
import {
    byPosition,
    changedSettingsCount,
    convertFieldsToEnabledFieldInputs,
    convertFieldsToFormData,
    convertFormDataToFieldInputs,
    DEFAULT_CUSTOM_FIELD_OPTIONS,
    FieldDefinition,
    FieldInput,
    FieldOption,
    FieldsFormValues,
    getFieldLabel,
    getOptionHelpText,
    getOptionLabel,
    groupFieldOptions,
    groupOf,
    isCustomFieldDefinition,
    isGroupField,
    moveField,
    NEW_CUSTOM_FIELD_PREFIX,
    REQUIRED_CUSTOM_FIELD_OPTIONS,
} from './fieldsConfigData.ts';
import { FieldsAllSettingsCard, OverviewDocumentConfiguration } from './FieldSettingsOverview.tsx';
import { SECTION_SETTINGS_PARAM, useSectionSettingsView } from './fieldSettingsOverviewData.ts';
import { useFieldFineTuning } from './fineTuning/FieldFineTuning.tsx';
import { RuleLevel } from './fineTuning/rules.ts';

const EllipsisIcon = withIcon(faEllipsis);

const HEADER_TRANSLATION_PREFIX = 'assistance:headerView.fieldNames';
const ITEM_TRANSLATION_PREFIX = 'assistance:itemsView.fieldNames';
const ASSISTANCE_PARAM = 'assistance';
// in the advanced settings these stay visible even while empty; choices is only listed for text
const ALWAYS_SHOWN_CUSTOM_FIELD_OPTIONS = [...REQUIRED_CUSTOM_FIELD_OPTIONS, 'choices'];

export interface FieldsSection {
    // none for the header
    itemType?: string;
    titleKey?: string;
    // a title the user gave the section (universal tables), instead of titleKey
    title?: string;
    // more items for the section's ⋯ menu
    menuItems?: React.ReactNode;
}

export const HEADER_SECTION: FieldsSection = { titleKey: 'configPage.document.fieldSections.header' };

export const itemSection = (itemType: string, titleKey: string): FieldsSection => ({ itemType, titleKey });

const keyOf = (section: FieldsSection) => section.itemType ?? 'header';

const titleOf = (t: TFunction, section: FieldsSection) => section.title ?? t(section.titleKey);

// a built-in section has its own string ("Line item settings"), so no translated title is spliced into a sentence;
// "{{section}} settings" is for the names users give universal tables
const sectionTextOf = (t: TFunction, section: FieldsSection, suffix: string, fallbackKey: string) =>
    t([section.titleKey && `${section.titleKey}${suffix}`, fallbackKey].filter(Boolean), {
        section: titleOf(t, section),
    });
// the settings view's title ("Header settings") and the section menu's item that opens it ("View header settings")
const settingsTitleOf = (t: TFunction, section: FieldsSection) =>
    sectionTextOf(t, section, 'Settings', 'configPage.document.overview.sectionSettings');
const viewSettingsLabelOf = (t: TFunction, section: FieldsSection) =>
    sectionTextOf(t, section, 'ViewSettings', 'configPage.document.overview.viewSectionSettings');

// the longest item type first, so "positions__article_sets__articles__…" isn't taken for a "positions" field
const sectionOf = (sections: FieldsSection[], fullName: string) =>
    sections
        .filter((section) => section.itemType)
        .sort((a, b) => b.itemType.length - a.itemType.length)
        .find(
            (section) => fullName === section.itemType || fullName.startsWith(`${section.itemType}${GROUP_SEPARATOR}`)
        ) ?? sections.find((section) => !section.itemType);

// a field as its section's editor names it: an item field without its item type
interface SectionField extends FieldDefinition {
    originalName: string;
}

const sectionFieldsOf = (sections: FieldsSection[], section: FieldsSection, fields: FieldDefinition[]) =>
    fields
        .filter((field) => !isGroupField(field) && sectionOf(sections, field.name) === section)
        .map(
            (field): SectionField => ({
                ...field,
                name:
                    field.name === section.itemType
                        ? field.name.split(GROUP_SEPARATOR).pop()
                        : field.name.replace(`${section.itemType}${GROUP_SEPARATOR}`, ''),
                originalName: field.name,
            })
        );

const translationPrefixOf = (section: FieldsSection) =>
    section.itemType ? ITEM_TRANSLATION_PREFIX : HEADER_TRANSLATION_PREFIX;

// line item rules only write to the default item type
const fineTuningLevelOf = (section: FieldsSection): RuleLevel | undefined => {
    if (!section.itemType) return 'header';
    return section.itemType === DEFAULT_LINE_ITEM_TYPE ? 'lineItem' : undefined;
};

// options with an editor of their own, by the field's name without its item type
const customComponentOf = (fieldName: string, name: string): React.ElementType | undefined => {
    const isDeliveryDate = fieldName === 'delivery_date' || fieldName === 'article_delivery_date';
    if (isDeliveryDate && name === 'delivery_date_range_replacement_weekday') {
        return DeliveryDateRangeReplacementSelectField;
    }
    if (isDeliveryDate && name === 'rules') return DateRuleListField;
    if (name === 'document_number_fallback') return DocumentNumberFallbackSelectField;
    return undefined;
};

const DATA_TYPES = ['string', 'decimal', 'integer', 'date', 'boolean'];

// a custom field's settings: the instructions as a text area, the data type as a select, the choices as tags, and the
// output name fixed once saved
const customFieldEditorOf = (t: TFunction, name: string, option: FieldOption) => {
    if (name === 'data_label_instructions') return { component: TextField, componentProps: { maxLength: 500 } };
    if (name === 'choices') return { component: TagsField, componentProps: {} };
    if (name === 'data_type') {
        return {
            component: SelectField,
            componentProps: {
                options: DATA_TYPES.map((value) => ({ label: t(`configPage.document.form.${value}`), value })),
                renderOption: (item: { label: string }) => item.label,
            },
        };
    }
    if (name === 'data_label_name' && option.value != null) {
        return { component: undefined, componentProps: { readOnly: true, disabled: true } };
    }
    return { component: undefined, componentProps: {} };
};

// active the way the option's switch shows it: a boolean that is on, or any other option with a value
const isOptionActive = (option: FieldOption | undefined, value: unknown) =>
    option?.valueType === 'boolean' ? !!value : value != undefined;

interface OptionFieldProps {
    form: UseFormReturn<FieldsFormValues>;
    fieldName: string;
    field: FieldDefinition;
    name: string;
    placeholder?: string;
    // the full names a custom field's output name must not lead to
    takenFullNames: string[];
    // what a custom field's output name is prefixed with in its full name
    outputNamePrefix: string;
}

const OptionField = ({
    form,
    fieldName,
    field,
    name,
    placeholder = undefined,
    takenFullNames,
    outputNamePrefix,
}: OptionFieldProps) => {
    const { t } = useTranslation('config');
    const option = field.options?.find((item) => item.name === name);
    if (!option) return null;

    const fieldPath = `${groupOf(fieldName)}.${fieldName}`;
    const isCustom = isCustomFieldDefinition(field);
    const required = isCustom && REQUIRED_CUSTOM_FIELD_OPTIONS.includes(name);
    const { component, componentProps } = isCustom
        ? customFieldEditorOf(t, name, option)
        : { component: customComponentOf(fieldName, name), componentProps: {} };

    const validate = (value: unknown) => {
        if (name !== 'data_label_name' || typeof value !== 'string') return true;
        // the backend reads "a__b" as subfield b of field a
        if (value.includes(GROUP_SEPARATOR)) return t('configPage.document.form.outputNameWithSeparator');
        if (takenFullNames.includes(`${outputNamePrefix}${value}`)) {
            return t('configPage.document.form.duplicatedField');
        }
        return true;
    };

    const handleValueChange = (onChange: (value: unknown) => void) => (value: unknown) => {
        if (name === 'data_label_name' && typeof value === 'string') {
            onChange(value.replace(/ /g, '_'));
            return;
        }
        // choices only apply to text
        if (name === 'data_type' && value !== 'string' && form.getValues(`${fieldPath}.choices`) != null) {
            form.setValue(`${fieldPath}.choices`, null);
        }
        onChange(value);
    };

    return (
        <Controller
            name={`${fieldPath}.${name}`}
            control={form.control}
            rules={{ required: required ? t('configPage.document.form.requiredField') : undefined, validate }}
            render={({ field: controller, fieldState }) => (
                <FieldsConfig.OptionsFormField
                    label={getOptionLabel(t, fieldName, name)}
                    helpText={getOptionHelpText(t, fieldName, name)}
                    error={fieldState.error}
                    defaultValue={option.defaultValue}
                    valueType={option.valueType}
                    nullable={isCustom ? false : (option.isNullable ?? true)}
                    required={required}
                    component={component}
                    componentProps={{ placeholder, ...componentProps }}
                    value={controller.value}
                    onValueChange={handleValueChange(controller.onChange)}
                    onBlur={controller.onBlur}
                />
            )}
        />
    );
};

interface FieldsSectionApi {
    hasChanges: () => boolean;
    // what the changes are to, for the discard question: the open field, else the section
    changesLabel: () => string;
}

interface FieldsSectionEditorProps {
    ref: React.Ref<FieldsSectionApi>;
    section: FieldsSection;
    // the section's saved fields
    sectionFields: SectionField[];
    // every saved field of the channel, which a custom field's output name must not collide with
    allFields: FieldDefinition[];
    documentType: DocumentType;
    channelId: string;
    fineTuningLevel?: RuleLevel;
    // the full name of the field or subfield whose page is open, when it belongs to this section
    openField: string | null;
    // a custom field added in this visit, whose page is open: it only exists there, until it's saved
    newCustomField: string | null;
    // another section's field page is open
    isHidden: boolean;
    onSave: (inputs: FieldInput[]) => Promise<unknown>;
    // takes the field, with its subfields, out of use and saves
    onRemove: (fullName: string) => void;
    onShowSettings: () => void;
    toField: (fullName: string | null, search?: string) => { pathname: string; search: string };
    // goes without asking, for a leave the page starts itself while its changes are still in the form
    navigateOnPurpose: (to: To) => void;
    onDiscard: () => void;
    // true while a fine-tuning rule opened from a field page has unsaved edits
    ruleChangesRef: React.RefObject<boolean>;
    getRuleUrl: (channelId: string, ruleId: string) => To;
    customFieldOptions: FieldOption[];
}

// one section of the list and its field pages. Its form and order start from the saved fields; a save or a discard
// mounts it anew. Leaving a page with changes asks first (the page's blocker)
const FieldsSectionEditor = ({
    ref,
    section,
    sectionFields,
    allFields,
    documentType,
    channelId,
    fineTuningLevel = undefined,
    openField,
    newCustomField,
    isHidden,
    onSave,
    onRemove,
    onShowSettings,
    toField,
    navigateOnPurpose,
    onDiscard,
    ruleChangesRef,
    getRuleUrl,
    customFieldOptions,
}: FieldsSectionEditorProps) => {
    const { t } = useTranslation('config');
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const translationPrefix = translationPrefixOf(section);
    const sectionTitle = titleOf(t, section);
    const allowsCustomFields = documentType !== DocumentType.ListOfServices;

    const savedFields = sectionFields.filter((field) => field.enabled);
    const savedSort = savedFields.toSorted(byPosition).map((field) => field.name);
    const form = useForm<FieldsFormValues>({
        defaultValues: {
            ...convertFieldsToFormData(savedFields),
            ...(newCustomField && {
                [newCustomField]: {
                    [newCustomField]: Object.fromEntries(
                        customFieldOptions.map((option) => [option.name, option.defaultValue])
                    ),
                },
            }),
        },
        mode: 'onTouched',
    });
    const [fieldsSort, setFieldsSort] = useState(newCustomField ? [...savedSort, newCustomField] : savedSort);
    const [isAddingFields, setAddingFields] = useState(false);
    const [isAddingSubfields, setAddingSubfields] = useState(false);
    const formValues = form.watch();
    const { isDirty, isSubmitting } = form.formState;
    const hasChanges = isDirty || !isEqual(fieldsSort, savedSort);

    // custom fields added in this visit are only in the form
    const newCustomFields: FieldDefinition[] = fieldsSort
        .filter((name) => name.startsWith(NEW_CUSTOM_FIELD_PREFIX))
        .map((name) => ({ name, enabled: true, options: customFieldOptions }));
    const fields: FieldDefinition[] = [...newCustomFields, ...sectionFields];
    const definitionOf = (name: string) => fields.find((field) => field.name === name);

    const fullNameOf = (name: string) =>
        sectionFields.find((field) => field.name === name)?.originalName ??
        (section.itemType ? `${section.itemType}${GROUP_SEPARATOR}${name}` : name);
    const nameOf = (fullName: string) =>
        sectionFields.find((field) => field.originalName === fullName)?.name ??
        fullName.replace(section.itemType ? `${section.itemType}${GROUP_SEPARATOR}` : '', '');
    const getLabel = (name: string) => getFieldLabel(t, name, fields, formValues, translationPrefix);
    const toName = (name: string | null) => toField(name && fullNameOf(name));

    const withSubfields = (name: string) =>
        fields.filter((field) => field.name === name || field.name.startsWith(`${name}${GROUP_SEPARATOR}`));

    // a field comes with its subfields and goes last, a subfield to the end of its field's block
    const addField = (name: string) => {
        const definitions = withSubfields(name).filter((field) => !fieldsSort.includes(field.name));
        definitions.forEach((definition) =>
            form.setValue(
                `${groupOf(definition.name)}.${definition.name}`,
                Object.fromEntries((definition.options ?? []).map((option) => [option.name, option.defaultValue])),
                { shouldDirty: true }
            )
        );
        const names = definitions.map((definition) => definition.name);
        setFieldsSort((sort) => {
            if (!name.includes(GROUP_SEPARATOR)) return [...sort, ...names];
            const blockEnd = sort.findLastIndex((item) => groupOf(item) === groupOf(name)) + 1;
            return [...sort.slice(0, blockEnd), ...names, ...sort.slice(blockEnd)];
        });
    };

    // a field goes with its subfields
    const removeField = (name: string) => {
        const names = withSubfields(name).map((field) => field.name);
        const group = groupOf(name);
        const groupValues = Object.fromEntries(
            Object.entries(form.getValues(group) ?? {}).filter(([fieldName]) => !names.includes(fieldName))
        );
        form.setValue(group, name === group ? undefined : groupValues, { shouldDirty: true });
        setFieldsSort((sort) => sort.filter((item) => !names.includes(item)));
    };

    const nextCustomFieldName = () => {
        const numbers = fieldsSort
            .filter((name) => name.startsWith(NEW_CUSTOM_FIELD_PREFIX))
            .map((name) => Number(name.slice(NEW_CUSTOM_FIELD_PREFIX.length)));
        return `${NEW_CUSTOM_FIELD_PREFIX}${Math.max(-1, ...numbers) + 1}`;
    };

    const openName = openField && nameOf(openField);
    const openBase = openName && groupOf(openName);
    const isOpenInUse = !!openName && fieldsSort.includes(openBase) && fieldsSort.includes(openName);

    const openLabel = isOpenInUse ? getLabel(openName) : undefined;
    useImperativeHandle(ref, () => ({ hasChanges: () => hasChanges, changesLabel: () => openLabel ?? sectionTitle }));

    const save = () =>
        form.handleSubmit((values) => onSave(convertFormDataToFieldInputs(values, fieldsSort, fullNameOf)))();

    const assistance = useFieldAssistance({
        documentType,
        channelId,
        group: section.itemType ? 'line_item' : 'header',
        getLabel,
    });

    const listCrumb = { label: t('configPage.navigation.fields'), onClick: () => navigate(toField(null)) };
    const sectionCrumb = { label: sectionTitle, onClick: listCrumb.onClick };
    const baseLabel = isOpenInUse ? getLabel(openBase) : '';

    const fineTuning = useFieldFineTuning({
        level: fineTuningLevel,
        channelId,
        documentType,
        channelFields: allFields,
        field: isOpenInUse && openName === openBase ? openField : null,
        fieldLabel: baseLabel,
        fieldPath: toName(openBase).pathname,
        parents: [listCrumb, sectionCrumb],
        hasChangesRef: ruleChangesRef,
        getRuleUrl,
    });

    if (isHidden) return null;

    if (openField) {
        if (!isOpenInUse) {
            return (
                <FieldsConfig.Page
                    parents={[listCrumb, sectionCrumb]}
                    current={t('configPage.document.fieldNotInUse')}
                />
            );
        }
        if (fineTuning.page) return fineTuning.page;

        const baseCrumb = { label: baseLabel, onClick: () => navigate(toName(openBase)) };
        const fieldAssistance =
            openName === openBase &&
            assistance.assistanceOf(openBase, () => navigate(toField(openField, `?${ASSISTANCE_PARAM}`)));
        if (fieldAssistance && searchParams.has(ASSISTANCE_PARAM)) {
            return (
                <FieldsConfig.Page
                    parents={[listCrumb, sectionCrumb, baseCrumb]}
                    current={fieldAssistance.title}
                    title={fieldAssistance.title}
                >
                    {fieldAssistance.details}
                </FieldsConfig.Page>
            );
        }

        // a field added in this visit isn't saved yet: Cancel takes it out, and there's nothing to remove
        const isUnsaved = !savedFields.some((field) => field.name === openBase);
        const definition = definitionOf(openName);
        const values = formValues[groupOf(openName)]?.[openName] ?? {};
        const { basic, advanced } = groupFieldOptions(definition, values);
        const outputNamePrefix = section.itemType ? `${section.itemType}${GROUP_SEPARATOR}` : '';
        // every other field of the channel, and the names the other new custom fields here will be saved under
        const takenFullNames = [
            ...allFields.map((field) => field.name).filter((name) => name !== fullNameOf(openName)),
            ...fieldsSort
                .filter((name) => name !== openName && isCustomFieldDefinition(definitionOf(name)))
                .map((name) => formValues[groupOf(name)]?.[name]?.data_label_name)
                .filter(Boolean)
                .map((outputName) => `${outputNamePrefix}${outputName}`),
        ];
        const renderOption = (name: string) => (
            <OptionField
                key={name}
                form={form}
                fieldName={openName}
                field={definition}
                name={name}
                placeholder={name === 'display_name' ? t(`${translationPrefix}.${openName}`) : undefined}
                takenFullNames={takenFullNames}
                outputNamePrefix={outputNamePrefix}
            />
        );
        const subfieldRows = fieldsSort
            .filter((name) => name.startsWith(`${openBase}${GROUP_SEPARATOR}`))
            .map((name) => ({
                name,
                label: getLabel(name),
                isRequired: !!(formValues[openBase]?.[name]?.is_required ?? true),
            }));
        const addableSubfields = withSubfields(openBase)
            .filter((field) => field.name !== openBase && !fieldsSort.includes(field.name))
            .map((field) => ({ name: field.name, label: getLabel(field.name) }))
            .sort((a, b) => a.label.localeCompare(b.label));

        return (
            <FieldsConfig.Page
                parents={openName === openBase ? [listCrumb, sectionCrumb] : [listCrumb, sectionCrumb, baseCrumb]}
                current={openLabel}
                title={openLabel}
                subtitle={isUnsaved ? t('configPage.notSavedYet') : undefined}
                actions={
                    !isUnsaved &&
                    openName === openBase && (
                        <FieldsConfig.RemoveMenu
                            fieldLabel={openLabel}
                            message={
                                isCustomFieldDefinition(definition)
                                    ? t('configPage.document.removeCustomFieldMessage')
                                    : t('configPage.document.removeFieldMessage')
                            }
                            onRemove={() => onRemove(openField)}
                        />
                    )
                }
            >
                {/* keyed, so each page's advanced settings start folded */}
                <React.Fragment key={openName}>
                    {fieldAssistance?.banner}
                    {basic.length > 0 && (
                        <FieldsConfig.Section title={t('configPage.document.form.basics')}>
                            <FieldsConfig.Card>{basic.map(renderOption)}</FieldsConfig.Card>
                        </FieldsConfig.Section>
                    )}
                    {openName === openBase && (subfieldRows.length > 0 || addableSubfields.length > 0) && (
                        <FieldsConfig.Subfields
                            fieldLabel={openLabel}
                            rows={subfieldRows}
                            toRow={toName}
                            onMove={(name, target) => setFieldsSort((sort) => moveField(sort, name, target))}
                            onRemove={removeField}
                            adding={{
                                items: addableSubfields,
                                label: t('configPage.document.form.moreSubfields', { count: addableSubfields.length }),
                                isOpen: isAddingSubfields,
                                onOpenChange: setAddingSubfields,
                                onAdd: addField,
                            }}
                        />
                    )}
                    {advanced.length > 0 && (
                        <FieldsConfig.AdvancedOptions
                            title={t('configPage.document.form.advancedSettings')}
                            rows={advanced.map((name) => ({
                                key: name,
                                isActive: isOptionActive(
                                    definition.options?.find((option) => option.name === name),
                                    values[name]
                                ),
                                isAlwaysShown:
                                    isCustomFieldDefinition(definition) &&
                                    ALWAYS_SHOWN_CUSTOM_FIELD_OPTIONS.includes(name),
                                node: renderOption(name),
                            }))}
                        />
                    )}
                    {hasChanges ? (
                        <FieldsConfig.SaveBar
                            isSaving={isSubmitting}
                            onSave={save}
                            onCancel={() => {
                                if (isUnsaved) navigateOnPurpose(toField(null));
                                onDiscard();
                            }}
                        />
                    ) : (
                        openName === openBase && fineTuning.entry
                    )}
                </React.Fragment>
            </FieldsConfig.Page>
        );
    }

    const hasOtherThanCustomFields = fields.some((field) => !isCustomFieldDefinition(field));
    const rows: FieldsConfigRowData[] = fieldsSort
        .filter((name) => !name.includes(GROUP_SEPARATOR))
        .map((name) => {
            const subfieldCount = fieldsSort.filter((item) => item.startsWith(`${name}${GROUP_SEPARATOR}`)).length;
            const changedCount = changedSettingsCount(
                savedFields.filter(
                    (field) =>
                        (field.name === name || field.name.startsWith(`${name}${GROUP_SEPARATOR}`)) &&
                        fieldsSort.includes(field.name)
                )
            );
            return {
                name,
                label: getLabel(name),
                note: [
                    hasOtherThanCustomFields &&
                        isCustomFieldDefinition(definitionOf(name)) &&
                        t('configPage.document.fieldNotes.customField'),
                    (formValues[name]?.[name]?.is_required ?? true)
                        ? t('configPage.document.fieldNotes.required')
                        : t('configPage.document.fieldNotes.optional'),
                    subfieldCount > 0 && t('configPage.document.fieldNotes.subfields', { count: subfieldCount }),
                    changedCount > 0 && t('configPage.document.fieldNotes.changedSettings', { count: changedCount }),
                ]
                    .filter(Boolean)
                    .join(' · '),
            };
        });
    const addableFields = fields
        .filter((field) => !field.name.includes(GROUP_SEPARATOR) && !fieldsSort.includes(field.name))
        .map((field) => ({ name: field.name, label: getLabel(field.name) }))
        .sort((a, b) => a.label.localeCompare(b.label));

    return (
        <SettingsCard
            className="pb-10"
            title={sectionTitle}
            description={t('configPage.document.fieldsIntro')}
            controls={
                <DropdownMenu>
                    <DropdownMenu.Trigger asChild>
                        <Button
                            type="button"
                            variant="ghost"
                            aria-label={t('configPage.document.moreSectionActions', { section: sectionTitle })}
                        >
                            <EllipsisIcon />
                        </Button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content align="end" className="min-w-48">
                        <DropdownMenu.Item onSelect={onShowSettings}>
                            {viewSettingsLabelOf(t, section)}
                        </DropdownMenu.Item>
                        {section.menuItems}
                    </DropdownMenu.Content>
                </DropdownMenu>
            }
        >
            <FieldsConfig.List
                rows={assistance.withAssistanceCounts(rows)}
                toRow={toName}
                onMove={(name, target) => setFieldsSort((sort) => moveField(sort, name, target))}
                onRemove={removeField}
                adding={{
                    items: addableFields,
                    label: t('configPage.document.form.moreFields', { count: addableFields.length }),
                    isOpen: isAddingFields,
                    onOpenChange: setAddingFields,
                    onAdd: addField,
                    // a custom field opens its own page, so with changes on the list the blocker asks first
                    onAddCustom: allowsCustomFields ? () => navigate(toName(nextCustomFieldName())) : undefined,
                }}
            />
            {hasChanges && <FieldsConfig.SaveBar isSaving={isSubmitting} onSave={save} onCancel={onDiscard} />}
        </SettingsCard>
    );
};

export interface FieldsDocumentConfiguration extends OverviewDocumentConfiguration {
    documentType: DocumentType;
    configPath: string;
}

interface FieldsPageProps {
    sections: FieldsSection[];
    config: { fields?: FieldDefinition[] } | undefined;
    documentConfiguration: FieldsDocumentConfiguration;
    channelId: string;
    hasFineTuning: boolean;
    onSubmit: (fieldConfigs: FieldInput[]) => Promise<unknown>;
    // what a new custom field starts with
    customFieldOptions?: FieldOption[];
    // below the sections on the list
    footer?: React.ReactNode;
}

// The Fields tab: a card per section (header fields, then each item type) listing its fields in the order of the
// assistance view, each field and subfield on its own page at /fields/<full field name>. Cancel and Save show up once
// something changed: a field page saves that field, a section's card its list
const FieldsPage = ({
    sections,
    config,
    documentConfiguration,
    channelId,
    hasFineTuning,
    onSubmit,
    customFieldOptions = DEFAULT_CUSTOM_FIELD_OPTIONS,
    footer = undefined,
}: FieldsPageProps) => {
    const { t } = useTranslation('config');
    const { publishToast } = useToaster();
    const navigate = useNavigate();
    const { tab, configId } = useParams();
    const { settingsSectionKey, setSettingsSection } = useSectionSettingsView();
    const [discardCounts, setDiscardCounts] = useState<Record<string, number>>({});
    const editors = useRef<Record<string, FieldsSectionApi>>({});
    const ruleChangesRef = useRef(false);
    const allFields = config?.fields ?? [];

    const toField = (fullName: string | null, search = '') => ({
        pathname: url(documentConfiguration.configPath, { channelId, tab, configId: fullName }),
        search,
    });
    const listPath = toField(null).pathname;
    const openSection = configId ? sectionOf(sections, configId) : undefined;
    const hasChangesIn = (section: FieldsSection) => !!editors.current[keyOf(section)]?.hasChanges();
    const discardIn = (affected: FieldsSection[]) =>
        setDiscardCounts((counts) => ({
            ...counts,
            ...Object.fromEntries(affected.map((section) => [keyOf(section), (counts[keyOf(section)] ?? 0) + 1])),
        }));

    // the sections whose changes a move drops: all of them when it leaves the tab or shows a section's settings;
    // between the list and the field pages only the ones left and opened, since the other sections keep their changes
    const sectionsLeftFor = (current: Location, next: Location) => {
        if (new URLSearchParams(next.search).has(SECTION_SETTINGS_PARAM)) return sections;
        if (next.pathname !== listPath && !next.pathname.startsWith(`${listPath}/`)) return sections;
        if (next.pathname === current.pathname) return [];
        const nameIn = ({ pathname }: Location) =>
            pathname === listPath ? null : decodeURIComponent(pathname.slice(listPath.length + 1));
        return [nameIn(current), nameIn(next)].filter(Boolean).map((name) => sectionOf(sections, name));
    };

    // a leave that the page starts itself while its changes are still in the form isn't held back by the blocker
    const isLeavingOnPurpose = useRef(false);
    const navigateOnPurpose = (to: To, options?: { replace?: boolean }) => {
        isLeavingOnPurpose.current = true;
        navigate(to, options);
        isLeavingOnPurpose.current = false;
    };

    // every way to another page asks first: a link, the breadcrumb, the sidebar, the browser's Back. A field's rule is
    // in the query (?fine-tuning=), so leaving it is also a change of the query. One blocker for both: a router only
    // keeps the last one
    const blockedFrom = useRef<Location | null>(null);
    const blocker = useBlocker(({ currentLocation, nextLocation }) => {
        if (isLeavingOnPurpose.current) return false;
        blockedFrom.current = currentLocation;
        const isNewPath = currentLocation.pathname !== nextLocation.pathname;
        if (ruleChangesRef.current && (isNewPath || currentLocation.search !== nextLocation.search)) return true;
        return sectionsLeftFor(currentLocation, nextLocation).some(hasChangesIn);
    });
    const blockedSections = () =>
        blocker.state === 'blocked' ? sectionsLeftFor(blockedFrom.current, blocker.location) : [];

    // about the one unit with changes where there is one: the open field or the section
    const blockedMessage = () => {
        if (ruleChangesRef.current) return t('configPage.fineTuning.discardMessage');
        const changed = blockedSections().filter(
            (section, index, all) => all.indexOf(section) === index && hasChangesIn(section)
        );
        return changed.length === 1
            ? t('configPage.document.discardMessage', { name: editors.current[keyOf(changed[0])].changesLabel() })
            : t('configPage.document.leaveMessage');
    };

    // reloading or closing the tab
    useBeforeUnload(
        useCallback(
            (event: BeforeUnloadEvent) => {
                if (sections.some(hasChangesIn)) event.preventDefault();
            },
            [sections]
        )
    );

    const handleSave = async (section: FieldsSection, inputs: FieldInput[]) => {
        // the backend replaces the whole list, so the other sections' fields go back as saved
        const sectionNames = new Set(sectionFieldsOf(sections, section, allFields).map((field) => field.originalName));
        const otherInputs = convertFieldsToEnabledFieldInputs(
            allFields.filter((field) => !sectionNames.has(field.name))
        );

        // the backend names a new custom field after its data_label_name (save_configured_custom_fields)
        const openInput = inputs.find((input) => input.fieldName === configId);
        const dataLabelName = openInput?.options.find((option) => option.name === 'data_label_name')?.value;
        const savedOpenField =
            dataLabelName && [...configId.split(GROUP_SEPARATOR).slice(0, -1), dataLabelName].join(GROUP_SEPARATOR);

        try {
            await onSubmit([...otherInputs, ...inputs]);
            if (savedOpenField && savedOpenField !== configId) {
                navigateOnPurpose(toField(savedOpenField), { replace: true });
            }
            publishToast({ description: t('configPage.document.updateSuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('configPage.document.updateError'), status: 'error' });
        }
    };

    // every saved field except this one and its subfields
    const handleRemove = async (fullName: string) => {
        const fields = allFields.filter(
            (field) => field.name !== fullName && !field.name.startsWith(`${fullName}${GROUP_SEPARATOR}`)
        );
        try {
            await onSubmit(convertFieldsToEnabledFieldInputs(fields));
            navigateOnPurpose(toField(null));
            publishToast({ description: t('configPage.document.updateSuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('configPage.document.updateError'), status: 'error' });
        }
    };

    const settingsSection = sections.find((section) => keyOf(section) === settingsSectionKey);
    if (settingsSection) {
        return (
            <FieldsAllSettingsCard
                title={settingsTitleOf(t, settingsSection)}
                translationPrefix={translationPrefixOf(settingsSection)}
                selectFields={(fields) => sectionFieldsOf(sections, settingsSection, fields)}
                config={config}
                documentConfiguration={documentConfiguration}
                channelId={channelId}
                onBack={() => setSettingsSection(null)}
            />
        );
    }

    return (
        // one DndProvider for the whole page: swapping the lists' and the subfields' own providers between the list
        // and a field page sets up a second HTML5 backend before the first one is torn down, which throws
        <DndProvider backend={HTML5Backend}>
            <div className="flex flex-col gap-7">
                {sections.map((section) => {
                    const sectionFields = sectionFieldsOf(sections, section, allFields);
                    const key = keyOf(section);
                    const openField = openSection === section ? configId : null;
                    // "Add field" opens a new custom field's page (after asking about unsaved changes); the section
                    // starts anew with the field in it, and without it once the page is left
                    const openName = section.itemType
                        ? openField?.replace(`${section.itemType}${GROUP_SEPARATOR}`, '')
                        : openField;
                    const newCustomField =
                        openName?.startsWith(NEW_CUSTOM_FIELD_PREFIX) &&
                        !sectionFields.some((field) => field.originalName === openField)
                            ? openName
                            : null;
                    return (
                        <FieldsSectionEditor
                            key={`${key}:${JSON.stringify(sectionFields)}:${discardCounts[key] ?? 0}:${newCustomField}`}
                            ref={(editor) => {
                                editors.current[key] = editor;
                            }}
                            section={section}
                            sectionFields={sectionFields}
                            allFields={allFields}
                            documentType={documentConfiguration.documentType}
                            channelId={channelId}
                            fineTuningLevel={hasFineTuning ? fineTuningLevelOf(section) : undefined}
                            openField={openField}
                            newCustomField={newCustomField}
                            isHidden={!!openSection && openSection !== section}
                            onSave={(inputs) => handleSave(section, inputs)}
                            onRemove={handleRemove}
                            onShowSettings={() => setSettingsSection(key)}
                            toField={toField}
                            navigateOnPurpose={navigateOnPurpose}
                            onDiscard={() => discardIn([section])}
                            ruleChangesRef={ruleChangesRef}
                            getRuleUrl={(otherChannelId, ruleId) =>
                                url(documentConfiguration.configPath, {
                                    channelId: otherChannelId,
                                    tab: 'fine-tuning',
                                    configId: ruleId,
                                })
                            }
                            customFieldOptions={customFieldOptions}
                        />
                    );
                })}
                {!configId && footer}
            </div>
            {blocker.state === 'blocked' && (
                <ConfirmModal
                    title={t('configPage.document.discardTitle')}
                    labelConfirm={t('configPage.document.discardConfirm')}
                    onConfirm={() => {
                        discardIn(blockedSections());
                        blocker.proceed();
                    }}
                    onCancel={() => blocker.reset()}
                    danger
                >
                    {blockedMessage()}
                </ConfirmModal>
            )}
        </DndProvider>
    );
};

export default FieldsPage;
